import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import AthleteHomePage from "./AthleteHomePage";
import AthleteOyoWorkouts from "./AthleteOyoWorkouts";
import AvailabilityModal from "./AvailabilityModal";
import OyoWorkoutModal from "./OyoWorkoutModal";
import PracticeCalendar from "./PracticeCalendar";
import PracticeModal from "./PracticeModal";
import { apiFetch } from "./api";

export default function AthleteView({
  user,
  activeTab,
  onNavigateToLineup,
  onNavigateToCalendar,
  onNavigateToWorkouts,
  initialOyoWorkoutId,
  onConsumeOyoWorkoutLink,
}) {
  const [practiceModal, setPracticeModal] = useState(null);
  const [practiceDetails, setPracticeDetails] = useState(null);
  const [loadingPracticeDetails, setLoadingPracticeDetails] = useState(false);
  const [oyoWorkoutModalId, setOyoWorkoutModalId] = useState(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [squads, setSquads] = useState([]);
  const [homeRefreshKey, setHomeRefreshKey] = useState(0);

  useEffect(() => {
    apiFetch("/squads")
      .then((data) => setSquads(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (initialOyoWorkoutId) {
      setOyoWorkoutModalId(initialOyoWorkoutId);
      onConsumeOyoWorkoutLink?.();
    }
  }, [initialOyoWorkoutId, onConsumeOyoWorkoutLink]);

  const userTeam = typeof user?.team === "string" ? user.team.trim() : "";
  const userId = user?.id ? Number(user.id) : null;

  const loadPracticeDetails = useCallback(
    async (practiceId, practice) => {
      if (!practiceId) return;
      try {
        setLoadingPracticeDetails(true);
        console.log("[AthleteView] Loading practice details for practiceId:", practiceId, "practice:", practice);

        // Athletes don't have access to /practices/:id endpoint
        // Use the practice data passed to the function
        const currentPractice = practice || practiceModal?.practice;
        const practiceDate = currentPractice?.date?.slice(0, 10);

        // For athletes, we don't need to fetch coach attendance or detailed attendance
        // Just fetch the RSVP status and lineups

        // Fetch athlete's RSVP status
        console.log("[AthleteView] Fetching RSVP - userId:", userId, "practiceDate:", practiceDate, "practiceId:", practiceId);
        if (userId && practiceDate) {
          try {
            const attendanceRows = await apiFetch(
              `/attendance/range?user=${userId}&from=${practiceDate}&to=${practiceDate}`
            );
            console.log("[AthleteView] Attendance rows received:", attendanceRows);
            const record = (Array.isArray(attendanceRows) ? attendanceRows : []).find(
              (row) => Number(row.practice_id) === Number(practiceId)
            );
            if (record) {
              console.log("[AthleteView] Found RSVP status:", record.status);
              setPracticeModal((prev) =>
                prev
                  ? {
                      ...prev,
                      status: record.status || null,
                      responseData: record.responseData || null,
                    }
                  : prev
              );
            } else {
              console.log("[AthleteView] No RSVP record found for practice", practiceId, "in attendance rows");
            }
          } catch (err) {
            console.warn("Could not fetch athlete RSVP status:", err);
          }
        } else {
          console.log("[AthleteView] Skipping RSVP fetch - missing userId or practiceDate");
        }

        // Fetch lineups if available — show indicator if any squad is published for this date
        let lineupStatuses = [];
        if (practiceDate && userTeam) {
          try {
            const status = await apiFetch(
              `/lineups/${practiceDate}/status?team=${encodeURIComponent(userTeam)}`
            );
            if (status?.published) {
              lineupStatuses = [{ ...status, team: userTeam }];
            }
          } catch (err) {
            console.warn("Could not fetch lineup status:", err);
          }
        }

        // Set minimal practice details for athlete view
        const details = {
          coaches: [], // Athletes don't see coach attendance
          attendance: null, // Athletes don't see attendance summary
          practiceInfo: currentPractice, // Use the practice data we already have
          workouts: [], // Athletes don't see workouts in modal
          lineupStatuses,
        };
        console.log("[AthleteView] Setting practice details:", details);
        setPracticeDetails(details);
      } catch (err) {
        console.error("Failed to load practice details:", err);
        setPracticeDetails(null);
      } finally {
        setLoadingPracticeDetails(false);
      }
    },
    [userTeam, userId, practiceModal]
  );

  const sendRSVP = useCallback(
    async (newStatus, practiceId, responses = null) => {
      if (!userId) return;
      if (!practiceId) return;

      try {
        await apiFetch(`/attendance/mine?as=${userId}`, {
          method: "POST",
          body: JSON.stringify({
            status: newStatus,
            practice_id: practiceId,
            responses: responses || null,
          }),
        });
      } catch (err) {
        console.error("sendRSVP error", err);
        return;
      }

    },
    [userId]
  );

  function openPracticeModal(practice, statusForDay) {
    console.log("[AthleteView] Opening practice modal with practice:", practice, "status:", statusForDay);
    setPracticeModal({
      practice,
      status: statusForDay || null,
      responseData: practice?.rsvpResponseData || null,
    });
    setPracticeDetails(null);
    if (practice?.id) {
      loadPracticeDetails(practice.id, practice);
    }
  }

  function closePracticeModal() {
    setPracticeModal(null);
    setPracticeDetails(null);
  }

  async function handlePracticeModalRsvp(nextStatus, responses) {
    const practiceId = practiceModal?.practice?.id;
    const practice = practiceModal?.practice;
    if (!practiceId) return;

    // Update UI immediately with flushSync to show visual feedback on mobile
    flushSync(() => {
      setPracticeModal((prev) =>
        prev ? { ...prev, status: nextStatus, responseData: responses || null } : prev
      );
    });

    // Force iOS Safari to repaint by triggering a layout recalculation
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => {
        // This empty RAF forces iOS to repaint
      });
    }

    // Then send the API request
    await sendRSVP(nextStatus, practiceId, responses);
    await loadPracticeDetails(practiceId, practice);
    setHomeRefreshKey((k) => k + 1);
  }

  function handleActivityPracticeClick(practiceId, practiceDate, practiceStartTime, squadName, practiceEndTime, practiceName, practiceLocation) {
    if (!practiceId) return;
    const basePractice = {
      id: practiceId,
      date: practiceDate || null,
      startTime: practiceStartTime || null,
      endTime: practiceEndTime || null,
      time: practiceStartTime || null,
      squad: squadName || null,
      name: practiceName || null,
      location: practiceLocation || null,
    };
    openPracticeModal(basePractice, null);
  }

  function handleActivityLineupClick(lineupDate) {
    if (lineupDate && typeof onNavigateToLineup === "function") {
      onNavigateToLineup(lineupDate);
    }
  }

  function renderPracticeModal() {
    if (!practiceModal?.practice) return null;
    const day = practiceModal.practice.date?.slice(0, 10);
    const title = day
      ? `Practice RSVP - ${new Date(`${day}T00:00:00`).toLocaleDateString(undefined, {
          weekday: "long",
          month: "short",
          day: "numeric",
        })}`
      : "Practice RSVP";

    return (
      <PracticeModal
        isOpen={Boolean(practiceModal)}
        onClose={closePracticeModal}
        practice={practiceModal.practice}
        loadingDetails={loadingPracticeDetails}
        practiceDetails={practiceDetails}
        showRsvpButtons
        rsvpStatus={practiceModal.status}
        rsvpResponseData={practiceModal.responseData}
        onRsvp={handlePracticeModalRsvp}
        modalTitle={title}
        squads={squads}
      />
    );
  }

  if (activeTab === "calendar") {
    return (
      <div className="stack">
        <PracticeCalendar
          squads={squads}
          coach={null}
          userId={userId}
          teamColors={{}}
          canEditPractices={false}
          onPracticeClick={(practice) => openPracticeModal(practice, null)}
        />
        {renderPracticeModal()}
      </div>
    );
  }

  // Home tab - use new AthleteHomePage
  if (activeTab === "home") {
    return (
      <div className="stack">
        <AthleteHomePage
          user={user}
          onRsvpClick={() => setShowAvailabilityModal(true)}
          onLogWorkoutClick={onNavigateToWorkouts}
          onPracticeClick={handleActivityPracticeClick}
          onLineupClick={handleActivityLineupClick}
          onOyoWorkoutClick={(workoutId) => setOyoWorkoutModalId(workoutId)}
          onMaintenanceClick={undefined}
          refreshKey={homeRefreshKey}
        />

        {renderPracticeModal()}

        {oyoWorkoutModalId && (
          <OyoWorkoutModal
            workoutId={oyoWorkoutModalId}
            onClose={() => setOyoWorkoutModalId(null)}
          />
        )}

        <AvailabilityModal
          user={user}
          userType="athlete"
          isOpen={showAvailabilityModal}
          onClose={() => {
            setShowAvailabilityModal(false);
            setHomeRefreshKey((k) => k + 1);
          }}
          onNavigateToCalendar={onNavigateToCalendar}
        />
      </div>
    );
  }

  // Workouts tab
  if (activeTab === "workouts") {
    return (
      <div className="stack">
        <AthleteOyoWorkouts user={user} />
      </div>
    );
  }

  return null;
}
